/** Utility functions for task hierarchy and movement, separated from React hook logic. */
// @ts-check
import { db, Timestamp, runTransaction } from '@/lib/firebase';
import { generateKeyBetween } from 'fractional-indexing';
import { updateChildrenPathsRecursively } from '@/features/tasks/utils/structure/taskStructureUtils';
import { getTaskBatch, commitBatch } from '@/services/taskMutations';
import {
  getTaskRef,
  getTask,
  getLastSiblingTask,
  reserveNextOrderKeyInTransaction,
} from '@/services/taskService';

/**
 * @param {string|null} id
 * @param {string} userId
 * @param {Function} [fetchTaskById]
 */
export const fetchParentInfo = async (id, userId, fetchTaskById) => {
  if (!id) return { materializedPath: '', status: null, contexts: [] };
  const p = fetchTaskById ? await fetchTaskById(id, userId) : await getTask(id);
  return {
    materializedPath: p?.materializedPath || '',
    status: p?.status || null,
    contexts: p?.contexts || [],
  };
};

/**
 * @param {string | undefined} key
 * @param {string | null | undefined} parentId
 * @param {string} userId
 * @param {import('firebase/firestore').Transaction} [transaction] - when
 *   provided, the key is reserved via the per-parent orderKey anchor inside
 *   the transaction, so concurrent appends to the same parent can't
 *   generate the same key (see reserveNextOrderKeyInTransaction).
 * @returns {Promise<string>}
 */
export const determineNewOrderKey = async (
  key,
  parentId,
  userId,
  transaction,
) => {
  if (key !== undefined) return key;
  const lastKey =
    (await getLastSiblingTask(parentId || null, userId))?.orderKey || null;
  if (transaction) {
    return reserveNextOrderKeyInTransaction(
      transaction,
      parentId || null,
      userId,
      lastKey,
    );
  }
  return generateKeyBetween(lastKey, null);
};

/**
 * @param {Task} currentTask
 * @param {string | null | undefined} parentId
 * @param {any} parentInfo
 * @returns {UpdateTask}
 */
export const determineMoveDetails = (currentTask, parentId, parentInfo) => {
  const targetParentId =
    parentId !== undefined ? parentId : currentTask.parentId;
  /** @type {UpdateTask} */
  const payload = {
    parentId: targetParentId || null,
    updatedAt: Timestamp.now(),
  };

  if (targetParentId) {
    if (['Active', 'Backlog'].includes(parentInfo.status))
      payload.status = parentInfo.status;
    if (!currentTask.contexts?.length && parentInfo.contexts?.length)
      payload.contexts = parentInfo.contexts;
  }
  return payload;
};

/**
 * @param {string} taskId
 * @param {{ parentId?: string | null, orderKey?: string }} params
 * @param {string} userId
 * @param {Function} [fetchTaskById]
 * @param {Task | null} [existingTask]
 */
export const executeMoveTask = async (
  taskId,
  params,
  userId,
  fetchTaskById,
  existingTask = null,
) => {
  if (!taskId || !userId) return;
  const { parentId, orderKey: newKey } = params;

  const currentTask = existingTask || (await getTask(taskId));
  if (!currentTask) return null;

  const targetParentId =
    parentId !== undefined ? parentId : currentTask.parentId;
  if (
    targetParentId === currentTask.parentId &&
    (newKey === undefined || newKey === currentTask.orderKey)
  )
    return true;

  const parentInfo = await fetchParentInfo(
    targetParentId,
    userId,
    fetchTaskById,
  );
  const payload = determineMoveDetails(currentTask, parentId, parentInfo);
  const taskRef = getTaskRef(taskId);

  // The orderKey is reserved and the task is written inside one transaction.
  // For appends (no explicit orderKey), reservation goes through the
  // per-parent anchor doc, so a concurrent append into the same parent
  // forces a retry instead of both writers computing the same key
  // (see Duplicate orderKey Bug Mechanism / reserveNextOrderKeyInTransaction).
  const newPath = await runTransaction(db, async (transaction) => {
    const orderKey = await determineNewOrderKey(
      newKey,
      targetParentId,
      userId,
      transaction,
    );
    const materializedPath = `${parentInfo.materializedPath}${orderKey}.`;
    transaction.update(taskRef, { ...payload, orderKey, materializedPath });
    return materializedPath;
  });

  const batch = getTaskBatch();
  await updateChildrenPathsRecursively(taskId, newPath, userId, batch);
  await commitBatch(batch);
  return true;
};
